/* eslint-disable react-hooks/set-state-in-effect -- dialogs reset fields when closed */
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AddLinkIcon from '@mui/icons-material/AddLink';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PublicIcon from '@mui/icons-material/Public';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UndoIcon from '@mui/icons-material/Undo';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  BEER_INGESTION_DERIVED_STATUSES,
  BEER_INGESTION_STATUS_FILTER_ALL,
  BEER_INGESTION_TRUST_TARGET_PERCENT,
  NO_SUITABLE_SOURCE_REASONS,
} from '../../utils/callableBeerConstants';
import {
  canMarkNoSuitableSource,
  formatBeerIngestionProgress,
  formatBeerIngestionSourceChipAriaLabel,
  formatBeerIngestionSourceChipLabel,
  formatBeerIngestionStatusLabel,
  formatNoSuitableSourceHoverText,
  formatNoSuitableSourceReasonLabel,
  getBeerIngestionStatusChipColor,
  getRowTrustActionState,
  isBeerIngestionTargetMet,
  isSafeExternalUrl,
} from '../../utils/brewery/beerIngestionStatus';
import { beerIngestionRowElementId } from '../../utils/brewery/beerIngestionScrollPosition';
import {
  BEER_INGESTION_MONITORING_FILTER_ALL,
  BEER_INGESTION_MONITORING_FILTERS,
  formatBeerIngestionMonitoringFilterLabel,
  formatBeerIngestionMonitoringSummary,
  formatBeerIngestionMonitoringTooltip,
  formatRelativeCheckTime,
  getBeerIngestionMonitoringHealth,
  getTrustedMonitoringSource,
} from '../../utils/brewery/beerIngestionMonitoring';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SensorsIcon from '@mui/icons-material/Sensors';

const DEFAULT_NO_SUITABLE_SOURCE_REASON = NO_SUITABLE_SOURCE_REASONS[0];

const SOURCE_ROW_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
};
const TRUSTED_STAR_SX = { fontSize: 16 };

export function BeerIngestionProgressBanner({ summary, monitoringSummary = null }) {
  const metTarget = isBeerIngestionTargetMet(summary);
  return (
    <Stack spacing={1}>
      <Alert severity={metTarget ? 'success' : 'warning'} role="status">
        {formatBeerIngestionProgress(summary, BEER_INGESTION_TRUST_TARGET_PERCENT)}
      </Alert>
      {monitoringSummary ? (
        <Alert severity={monitoringSummary.failing > 0 ? 'warning' : 'info'} role="status">
          {formatBeerIngestionMonitoringSummary(monitoringSummary)}
        </Alert>
      ) : null}
      {monitoringSummary?.unmonitoredTrusted > 0 ? (
        <Alert severity="warning">
          {monitoringSummary.unmonitoredTrusted} trusted official source
          {monitoringSummary.unmonitoredTrusted === 1 ? ' has' : 's have'} no Firecrawl
          monitor yet.
        </Alert>
      ) : null}
    </Stack>
  );
}

BeerIngestionProgressBanner.propTypes = {
  summary: PropTypes.object.isRequired,
  monitoringSummary: PropTypes.object,
};

export function BeerIngestionFilterBar({
  statusFilter,
  hasWebsite,
  showExcluded,
  monitoringFilter = BEER_INGESTION_MONITORING_FILTER_ALL,
  onStatusFilterChange,
  onHasWebsiteChange,
  onShowExcludedChange,
  onMonitoringFilterChange,
}) {
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="beer-ingestion-status-filter-label">Status</InputLabel>
        <Select
          labelId="beer-ingestion-status-filter-label"
          label="Status"
          value={statusFilter}
          onChange={onStatusFilterChange}
        >
          <MenuItem value={BEER_INGESTION_STATUS_FILTER_ALL}>All in-scope</MenuItem>
          {BEER_INGESTION_DERIVED_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {formatBeerIngestionStatusLabel(status)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="beer-ingestion-monitoring-filter-label">Monitoring</InputLabel>
        <Select
          labelId="beer-ingestion-monitoring-filter-label"
          label="Monitoring"
          value={monitoringFilter}
          onChange={onMonitoringFilterChange}
        >
          <MenuItem value={BEER_INGESTION_MONITORING_FILTER_ALL}>All monitoring</MenuItem>
          {BEER_INGESTION_MONITORING_FILTERS.map((filterValue) => (
            <MenuItem key={filterValue} value={filterValue}>
              {formatBeerIngestionMonitoringFilterLabel(filterValue)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={<Checkbox checked={hasWebsite} onChange={onHasWebsiteChange} />}
        label="Has website"
      />
      <FormControlLabel
        control={<Checkbox checked={showExcluded} onChange={onShowExcludedChange} />}
        label="Show excluded"
      />
    </Stack>
  );
}

BeerIngestionFilterBar.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  hasWebsite: PropTypes.bool.isRequired,
  showExcluded: PropTypes.bool.isRequired,
  monitoringFilter: PropTypes.string,
  onStatusFilterChange: PropTypes.func.isRequired,
  onHasWebsiteChange: PropTypes.func.isRequired,
  onShowExcludedChange: PropTypes.func.isRequired,
  onMonitoringFilterChange: PropTypes.func.isRequired,
};

export function BreweryNameCell({ name, breweryId, onRowActionClick, openInNewTab = false }) {
  const displayName = name || breweryId;
  if (!breweryId) {
    return displayName;
  }
  const newTabProps = openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};
  return (
    <RouterLink
      to={`/brewery/${breweryId}`}
      data-action="open"
      data-brewery-id={breweryId}
      onClick={onRowActionClick}
      {...newTabProps}
    >
      {displayName}
    </RouterLink>
  );
}

BreweryNameCell.propTypes = {
  name: PropTypes.string,
  breweryId: PropTypes.string,
  onRowActionClick: PropTypes.func,
  openInNewTab: PropTypes.bool,
};

function SourceRow({ source }) {
  const label = formatBeerIngestionSourceChipLabel(source);
  const tooltipTitle = source.url || label;
  const ariaLabel = formatBeerIngestionSourceChipAriaLabel(source);
  const isLink = isSafeExternalUrl(source.url);
  const trustedStar = source.isTrusted ? (
    <StarIcon color="warning" aria-hidden sx={TRUSTED_STAR_SX} />
  ) : null;

  if (isLink) {
    return (
      <Tooltip title={tooltipTitle}>
        <Link
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          aria-label={ariaLabel}
          sx={SOURCE_ROW_SX}
        >
          {trustedStar}
          {label}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipTitle}>
      <Typography component="span" variant="body2" aria-label={ariaLabel} sx={SOURCE_ROW_SX}>
        {trustedStar}
        {label}
      </Typography>
    </Tooltip>
  );
}

SourceRow.propTypes = {
  source: PropTypes.object.isRequired,
};

function SourceList({ sources }) {
  if (!sources || sources.length === 0) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }
  return (
    <Stack direction="column" spacing={0.5} alignItems="flex-start">
      {sources.map((source) => (
        <SourceRow key={source.id || source.url} source={source} />
      ))}
    </Stack>
  );
}

SourceList.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.object),
};

function StatusChip({ row }) {
  const label = formatBeerIngestionStatusLabel(row.derivedStatus);
  const reasonHover =
    row.derivedStatus === 'no_suitable_source'
      ? formatNoSuitableSourceHoverText(row.classification)
      : '';
  const chip = (
    <Chip
      size="small"
      color={getBeerIngestionStatusChipColor(row.derivedStatus)}
      label={label}
      aria-label={reasonHover ? `${label}: ${reasonHover}` : undefined}
    />
  );
  if (!reasonHover) {
    return chip;
  }
  return (
    <Tooltip title={reasonHover} enterDelay={0}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {chip}
      </Box>
    </Tooltip>
  );
}

StatusChip.propTypes = {
  row: PropTypes.object.isRequired,
};

function MonitoringHealthChip({ row }) {
  const health = getBeerIngestionMonitoringHealth(row);
  const source = getTrustedMonitoringSource(row);
  const relative =
    health.key === 'up_to_date' || health.key === 'changed' || health.key === 'failing'
      ? formatRelativeCheckTime(source?.lastCheckedAt)
      : '';
  const label = relative ? `${health.label} · ${relative}` : health.label;
  const chip = (
    <Chip size="small" color={health.color} label={label} />
  );
  const tooltipTitle = health.tooltip || formatBeerIngestionMonitoringTooltip(source);
  return (
    <Tooltip title={tooltipTitle}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {chip}
      </Box>
    </Tooltip>
  );
}

MonitoringHealthChip.propTypes = {
  row: PropTypes.object.isRequired,
};

function isPendingRowAction(pendingAction, breweryId, action) {
  return pendingAction?.breweryId === breweryId && pendingAction?.action === action;
}

function rowActionButtonComponent(href) {
  if (href) {
    return 'a';
  }
  return 'button';
}

function RowActionButton({
  label,
  tooltip,
  action,
  breweryId,
  sourceId,
  disabled = false,
  loading = false,
  href,
  onRowActionClick,
  children,
}) {
  const buttonComponent = rowActionButtonComponent(href);
  return (
    <Tooltip title={tooltip || label}>
      <Box component="span">
        <IconButton
          size="small"
          component={buttonComponent}
          href={href}
          target={href ? '_blank' : undefined}
          rel={href ? 'noopener noreferrer' : undefined}
          disabled={!href && (disabled || loading)}
          aria-label={label}
          aria-busy={loading || undefined}
          data-action={action}
          data-brewery-id={breweryId}
          data-source-id={sourceId || ''}
          onClick={onRowActionClick}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : children}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

RowActionButton.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  action: PropTypes.string.isRequired,
  breweryId: PropTypes.string.isRequired,
  sourceId: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  href: PropTypes.string,
  onRowActionClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

function BeerIngestionRowActions({ row, actionLoading, pendingAction, onRowActionClick }) {
  const trustState = getRowTrustActionState(row);
  const isExcluded = row.derivedStatus === 'excluded';
  const noSourceEnabled = canMarkNoSuitableSource(row);
  const websiteUrl = isSafeExternalUrl(row.website) ? row.website : '';
  const trustedSource = getTrustedMonitoringSource(row);
  const isTrusted = row.derivedStatus === 'trusted' && Boolean(trustedSource);
  const canCheckNow = isTrusted && Boolean(trustedSource?.monitorId);
  const canStartMonitoring = isTrusted && !trustedSource?.monitorId;
  const canPause =
    isTrusted &&
    Boolean(trustedSource?.monitorId) &&
    trustedSource?.monitoringStatus === 'active';
  const canResume = isTrusted && trustedSource?.monitoringStatus === 'paused';
  const canRecreateMonitor = canPause;

  return (
    <Stack direction="row" spacing={0.25} flexWrap="nowrap">
      <RowActionButton
        label="Add source"
        action="add-source"
        breweryId={row.breweryId}
        disabled={actionLoading}
        onRowActionClick={onRowActionClick}
      >
        <AddLinkIcon fontSize="small" />
      </RowActionButton>
      <RowActionButton
        label="Trust"
        tooltip={trustState.reason || 'Trust'}
        action="trust"
        breweryId={row.breweryId}
        sourceId={trustState.sourceId}
        disabled={!trustState.enabled || actionLoading}
        loading={isPendingRowAction(pendingAction, row.breweryId, 'trust')}
        onRowActionClick={onRowActionClick}
      >
        <StarBorderIcon fontSize="small" />
      </RowActionButton>
      {row.derivedStatus === 'no_suitable_source' ? (
        <RowActionButton
          label="Clear no-source"
          action="clear-no-source"
          breweryId={row.breweryId}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'clear-no-source')}
          onRowActionClick={onRowActionClick}
        >
          <UndoIcon fontSize="small" />
        </RowActionButton>
      ) : (
        <RowActionButton
          label="No suitable source"
          action="no-source"
          breweryId={row.breweryId}
          disabled={!noSourceEnabled || actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'no-source')}
          onRowActionClick={onRowActionClick}
        >
          <BlockIcon fontSize="small" />
        </RowActionButton>
      )}
      <RowActionButton
        label={isExcluded ? 'Include' : 'Exclude'}
        action={isExcluded ? 'include' : 'exclude'}
        breweryId={row.breweryId}
        disabled={actionLoading}
        loading={isPendingRowAction(
          pendingAction,
          row.breweryId,
          isExcluded ? 'include' : 'exclude',
        )}
        onRowActionClick={onRowActionClick}
      >
        {isExcluded ? (
          <CheckCircleOutlineIcon fontSize="small" />
        ) : (
          <RemoveCircleOutlineIcon fontSize="small" />
        )}
      </RowActionButton>
      <RowActionButton
        label="Open website"
        tooltip={websiteUrl || 'No website'}
        action="open-website"
        breweryId={row.breweryId}
        href={websiteUrl || undefined}
        disabled={!websiteUrl}
        onRowActionClick={onRowActionClick}
      >
        <PublicIcon fontSize="small" />
      </RowActionButton>
      {canStartMonitoring ? (
        <RowActionButton
          label="Start monitoring"
          action="start-monitoring"
          breweryId={row.breweryId}
          sourceId={trustedSource?.id}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'start-monitoring')}
          onRowActionClick={onRowActionClick}
        >
          <SensorsIcon fontSize="small" />
        </RowActionButton>
      ) : null}
      {canCheckNow ? (
        <RowActionButton
          label="Check now"
          action="check-now"
          breweryId={row.breweryId}
          sourceId={trustedSource?.id}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'check-now')}
          onRowActionClick={onRowActionClick}
        >
          <RefreshIcon fontSize="small" />
        </RowActionButton>
      ) : null}
      {canRecreateMonitor ? (
        <RowActionButton
          label="Recreate monitor"
          tooltip="Delete Firecrawl's baseline, create a new monitor, and run a check"
          action="recreate-monitor"
          breweryId={row.breweryId}
          sourceId={trustedSource?.id}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'recreate-monitor')}
          onRowActionClick={onRowActionClick}
        >
          <RestartAltIcon fontSize="small" />
        </RowActionButton>
      ) : null}
      {canResume ? (
        <RowActionButton
          label="Resume"
          action="resume"
          breweryId={row.breweryId}
          sourceId={trustedSource?.id}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'resume')}
          onRowActionClick={onRowActionClick}
        >
          <PlayArrowIcon fontSize="small" />
        </RowActionButton>
      ) : null}
      {canPause ? (
        <RowActionButton
          label="Pause"
          action="pause"
          breweryId={row.breweryId}
          sourceId={trustedSource?.id}
          disabled={actionLoading}
          loading={isPendingRowAction(pendingAction, row.breweryId, 'pause')}
          onRowActionClick={onRowActionClick}
        >
          <PauseIcon fontSize="small" />
        </RowActionButton>
      ) : null}
    </Stack>
  );
}

BeerIngestionRowActions.propTypes = {
  row: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    breweryId: PropTypes.string,
    action: PropTypes.string,
  }),
  onRowActionClick: PropTypes.func.isRequired,
};

function BeerIngestionRow({ row, actionLoading, pendingAction, onRowActionClick }) {
  return (
    <TableRow hover id={beerIngestionRowElementId(row.breweryId)}>
      <TableCell>
        <BreweryNameCell
          name={row.name}
          breweryId={row.breweryId}
          onRowActionClick={onRowActionClick}
        />
      </TableCell>
      <TableCell>{row.city || '—'}</TableCell>
      <TableCell>
        <SourceList sources={row.sources} />
      </TableCell>
      <TableCell>
        <StatusChip row={row} />
      </TableCell>
      <TableCell>
        <MonitoringHealthChip row={row} />
      </TableCell>
      <TableCell>
        <BeerIngestionRowActions
          row={row}
          actionLoading={actionLoading}
          pendingAction={pendingAction}
          onRowActionClick={onRowActionClick}
        />
      </TableCell>
    </TableRow>
  );
}

BeerIngestionRow.propTypes = {
  row: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    breweryId: PropTypes.string,
    action: PropTypes.string,
  }),
  onRowActionClick: PropTypes.func.isRequired,
};

export function BeerIngestionTable({
  rows,
  loading,
  error,
  emptyMessage,
  actionLoading,
  pendingAction = null,
  onRowActionClick,
}) {
  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{String(error)}</Alert>;
  }

  if (rows.length === 0) {
    return (
      <Typography color="text.secondary">
        {emptyMessage || 'No breweries match the current filters.'}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="Beer ingestion">
        <BeerIngestionTableHead />
        <TableBody>
          {rows.map((row) => (
            <BeerIngestionRow
              key={row.breweryId}
              row={row}
              actionLoading={actionLoading}
              pendingAction={pendingAction}
              onRowActionClick={onRowActionClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BeerIngestionTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Brewery</TableCell>
        <TableCell>City</TableCell>
        <TableCell>Sources</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Monitoring</TableCell>
        <TableCell>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

BeerIngestionTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  emptyMessage: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  pendingAction: PropTypes.shape({
    breweryId: PropTypes.string,
    action: PropTypes.string,
  }),
  onRowActionClick: PropTypes.func.isRequired,
};

function NoSuitableSourceDialogFields({
  actionError,
  reason,
  notes,
  onReasonChange,
  onNotesChange,
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <FormControl fullWidth>
        <InputLabel id="no-suitable-source-reason-label">Reason</InputLabel>
        <Select
          labelId="no-suitable-source-reason-label"
          label="Reason"
          value={reason}
          onChange={onReasonChange}
        >
          {NO_SUITABLE_SOURCE_REASONS.map((reasonCode) => (
            <MenuItem key={reasonCode} value={reasonCode}>
              {formatNoSuitableSourceReasonLabel(reasonCode)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Notes"
        value={notes}
        onChange={onNotesChange}
        fullWidth
        multiline
        minRows={2}
      />
    </Stack>
  );
}

NoSuitableSourceDialogFields.propTypes = {
  actionError: PropTypes.string,
  reason: PropTypes.string.isRequired,
  notes: PropTypes.string.isRequired,
  onReasonChange: PropTypes.func.isRequired,
  onNotesChange: PropTypes.func.isRequired,
};

export function NoSuitableSourceDialog({
  open,
  actionError,
  actionLoading,
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = useState(DEFAULT_NO_SUITABLE_SOURCE_REASON);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setReason(DEFAULT_NO_SUITABLE_SOURCE_REASON);
      setNotes('');
    }
  }, [open]);

  const handleReasonChange = useCallback((event) => {
    setReason(event.target.value);
  }, []);

  const handleNotesChange = useCallback((event) => {
    setNotes(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({ reason, notes: notes.trim() || null });
  }, [notes, onSubmit, reason]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark no suitable source</DialogTitle>
      <DialogContent>
        <NoSuitableSourceDialogFields
          actionError={actionError}
          reason={reason}
          notes={notes}
          onReasonChange={handleReasonChange}
          onNotesChange={handleNotesChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={actionLoading}
          variant="contained"
          startIcon={
            actionLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NoSuitableSourceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

function ExcludeBreweryDialogFields({ actionError, reason, onReasonChange }) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <TextField
        label="Reason"
        value={reason}
        onChange={onReasonChange}
        fullWidth
        required
        multiline
        minRows={2}
      />
    </Stack>
  );
}

ExcludeBreweryDialogFields.propTypes = {
  actionError: PropTypes.string,
  reason: PropTypes.string.isRequired,
  onReasonChange: PropTypes.func.isRequired,
};

export function ExcludeBreweryDialog({
  open,
  actionError,
  actionLoading,
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const handleReasonChange = useCallback((event) => {
    setReason(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({ reason: reason.trim() });
  }, [onSubmit, reason]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Exclude brewery</DialogTitle>
      <DialogContent>
        <ExcludeBreweryDialogFields
          actionError={actionError}
          reason={reason}
          onReasonChange={handleReasonChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={actionLoading || !reason.trim()}
          variant="contained"
          startIcon={
            actionLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Exclude
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ExcludeBreweryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export function RecreateMonitorDialog({
  open,
  breweryName,
  actionError,
  actionLoading,
  onClose,
  onConfirm,
}) {
  const subject = breweryName ? ` for ${breweryName}` : '';
  const handleClose = useCallback(() => {
    if (actionLoading) {
      return;
    }
    onClose();
  }, [actionLoading, onClose]);
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Recreate monitor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>
            This deletes Firecrawl&apos;s baseline{subject}, creates a new monitor,
            and runs a check so beers can be extracted again.
          </Typography>
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={actionLoading}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={actionLoading}
          variant="contained"
          startIcon={
            actionLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Recreate monitor
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RecreateMonitorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  breweryName: PropTypes.string,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
